import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import { apiFetch } from "./client";

export interface DoseChangeHold {
  id: string;
  patient_id: string;
  product_id: string;
  source_channel: string;
  conversation_id: string;
  source_turn_id: string;
  source_message: string;
  /** The patient's earlier messages from the same chat, oldest first, kept
   * when the request was recorded (display only; absent on older requests). */
  source_context?: { text: string; at: string | null }[] | null;
  assigned_prescriber_id: string | null;
  assigned_prescriber_name?: string | null;
  status: "open" | "resolved" | "withdrawn";
  created_at: string;
  reconciliation_required: boolean;
  resolution: {
    decision?: string;
    note?: string;
    /** Set when status is "withdrawn". */
    reason?: WithdrawReason;
    duplicate_of?: string | null;
    /** A repeat request folded into the one already open. */
    automatic?: boolean;
    [key: string]: unknown;
  } | null;
  resolved_at: string | null;
  resolution_version: number;
}
export interface DoseChangeHoldsResponse {
  enabled: boolean;
  items: DoseChangeHold[];
  can_resolve: boolean;
  can_withdraw?: boolean;
}
/** Clinical staff clear a duplicate (the hold stays) or close a request without
 * a dose change: a dose we don't offer, or a message that wasn't a dose
 * request (the current prescription continues). */
export type ReleaseReason = "no_change_available" | "not_a_dose_request";
export type WithdrawReason = "duplicate" | ReleaseReason;
export type WithdrawDoseChangeHold =
  | {
      reason: "duplicate";
      note: string;
      duplicate_of: string;
      expected_resolution_version: number;
    }
  | {
      reason: ReleaseReason;
      note: string;
      confirmed: true;
      expected_resolution_version: number;
    };
export interface FoldedDoseRequest {
  id: string;
  source_channel: string;
  source_message: string;
  created_at: string;
}
export interface ResolveDoseChangeHold {
  decision: "updated_prescription" | "continue_existing";
  order_id: string;
  medication_id: string;
  note: string;
  confirmed: true;
  expected_resolution_version: number;
}
export function useDoseChangeHolds(patientId: string, readOnly = false) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: [
      "dose-change-holds",
      userId,
      readOnly ? "admin" : "clinician",
      patientId,
    ],
    queryFn: () =>
      apiFetch<DoseChangeHoldsResponse>(
        `${readOnly ? "/admin/clinical-admin" : "/clinician"}/patients/${encodeURIComponent(patientId)}/dose-change-holds`,
      ),
    enabled: Boolean(userId && patientId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
export function useResolveDoseChangeHold(patientId: string) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: ResolveDoseChangeHold;
    }) => {
      if (!userId) throw new Error("Sign in to resolve a dose-change hold.");
      return apiFetch<{ item: DoseChangeHold }>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/resolve`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["dose-change-holds", userId] }),
        qc.invalidateQueries({ queryKey: ["clinician"] }),
        qc.invalidateQueries({
          queryKey: ["admin", "clinical-admin", "patient", patientId],
        }),
      ]);
    },
  });
}

export function useWithdrawDoseChangeHold(patientId: string) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: WithdrawDoseChangeHold;
    }) => {
      if (!userId) throw new Error("Sign in to clear a dose-change request.");
      return apiFetch<{ item: DoseChangeHold & { still_held: boolean } }>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/withdraw`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["dose-change-holds", userId] }),
        qc.invalidateQueries({ queryKey: ["care-team-notes"] }),
        qc.invalidateQueries({ queryKey: ["clinician"] }),
        qc.invalidateQueries({
          queryKey: ["admin", "clinical-admin", "patient", patientId],
        }),
      ]);
    },
  });
}

export interface DoseChangeHandledCheck {
  available: boolean;
  reason?: string;
  order_id?: string;
  order_number?: string | null;
  medication_id?: string;
  medication_name?: string | null;
  directions?: string | null;
  previous_medication_name?: string | null;
  previous_directions?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
}

/** Whether a prescriber already answered this request with a new prescription. */
export function useDoseChangeHandledCheck(
  patientId: string,
  requestId: string,
  enabled: boolean,
) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: ["dose-change-holds", userId, "handled", patientId, requestId],
    queryFn: () =>
      apiFetch<DoseChangeHandledCheck>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/handled`,
      ),
    enabled: Boolean(enabled && userId && patientId && requestId),
    staleTime: 15_000,
  });
}

export function useMarkDoseChangeHandled(patientId: string) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: {
        confirmed: true;
        note: string;
        expected_resolution_version: number;
        expected_order_id: string;
        expected_medication_id: string;
      };
    }) => {
      if (!userId) throw new Error("Sign in to update a dose-change request.");
      return apiFetch<{ item: DoseChangeHold }>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/handled`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["dose-change-holds", userId] }),
        qc.invalidateQueries({ queryKey: ["care-team-notes"] }),
        qc.invalidateQueries({ queryKey: ["clinician"] }),
        qc.invalidateQueries({
          queryKey: ["admin", "clinical-admin", "patient", patientId],
        }),
      ]);
    },
  });
}

export function useDoseChangeHoldPrescriptions(
  patientId: string,
  enabled: boolean,
) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: ["dose-change-hold-prescriptions", userId, patientId],
    queryFn: () =>
      apiFetch<{ items: { id: string; name: string }[] }>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/prescriptions`,
      ),
    enabled: Boolean(enabled && userId && patientId),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function usePendingDoseChangeHolds(offset: number) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: ["dose-change-holds", userId, "pending", offset],
    queryFn: () =>
      apiFetch<{
        items: (DoseChangeHold & {
          patient_display_name: string | null;
          folded?: FoldedDoseRequest[];
        })[];
        has_more: boolean;
        enabled: boolean;
        can_withdraw?: boolean;
        /** False for viewers who see the queue but can't act (supervisors, nurses). */
        can_resolve?: boolean;
      }>(`/clinician/dose-change-holds?offset=${offset}&limit=50`),
    enabled: Boolean(userId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** What the prescriber can do with one open request from the worklist panel. */
export interface DoseChangeReview {
  request: DoseChangeHold;
  active_prescription: {
    medication_id: string;
    name: string;
    directions: string;
    product_id: string;
    /** Echoed back on keep so a replaced prescription is never bound unseen. */
    instructions_sha256: string;
  } | null;
  keep: {
    available: boolean;
    reason: string | null;
    order_id: string | null;
    order_number: string | null;
  };
  awaiting_order: {
    id: string;
    order_number: string | null;
    product_id: string;
  } | null;
}

export function useDoseChangeReview(patientId: string, requestId: string) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: ["dose-change-holds", userId, "review", patientId, requestId],
    queryFn: () =>
      apiFetch<DoseChangeReview>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/review`,
      ),
    enabled: Boolean(userId && patientId && requestId),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** Keep the current dose/directions; the server binds the exact active Rx. */
export function useKeepDoseChangeHold(patientId: string) {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      body: {
        note: string;
        confirmed: true;
        expected_resolution_version: number;
        expected_medication_id: string;
        expected_instructions_sha256: string;
      };
    }) => {
      if (!userId) throw new Error("Sign in to resolve a dose-change request.");
      return apiFetch<{ item: DoseChangeHold }>(
        `/clinician/patients/${encodeURIComponent(patientId)}/dose-change-holds/${encodeURIComponent(requestId)}/keep`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["dose-change-holds", userId] }),
        qc.invalidateQueries({ queryKey: ["clinician"] }),
        qc.invalidateQueries({
          queryKey: ["admin", "clinical-admin", "patient", patientId],
        }),
      ]);
    },
  });
}
